import {useRef, useEffect, useState, useCallback} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import {authApi} from '../redux/services/auth'
import {setAccessToken} from '../redux/slices/user.slice'

let baseUrl = process.env.BACKEND_URL
baseUrl = baseUrl.replace('api', 'ws')
baseUrl = baseUrl.replace('https', 'wss')
baseUrl = baseUrl.replace('http', 'ws')

export const useWS = ({token = null, stationId = null, skipAuth = false} = {}) => {
	const ws = useRef(null)
	const pingIntervalId = useRef(null)
	const reconnectTimeoutId = useRef(null)
	const reconnectDelay = useRef(1000)
	const shouldReconnect = useRef(true)
	const latestSocketUrl = useRef(null)
	const isRefreshingToken = useRef(false)
	
	const [socketInstance, setSocketInstance] = useState(null)
	const [station, setStation] = useState(stationId)
	
	const dispatch = useDispatch()
	const access_token = useSelector((state) => state.user.access_token)
	
	useEffect(() => {
		if (token && token !== access_token) {
			dispatch(setAccessToken(token))
		}
	}, [token, access_token, dispatch])
	
	const buildSocketUrl = useCallback((accessToken, stationParam) => {
		if (!skipAuth && accessToken && stationParam) {
			return `${baseUrl}/?token=${accessToken}&station=${stationParam}`
		} else if (!skipAuth && accessToken) {
			return `${baseUrl}/?token=${accessToken}`
		} else {
			return baseUrl
		}
	}, [skipAuth])
	
	const reconnectWithNewToken = useCallback(async () => {
		if (isRefreshingToken.current) {
			console.warn('🔄 Token refresh already in progress...')
			return
		}
		
		isRefreshingToken.current = true
		console.warn('🔄 Attempting token refresh...')
		
		try {
			const result = await dispatch(authApi.endpoints.refreshToken.initiate()).unwrap()
			const newToken = result?.access_token
			
			if (newToken) {
				console.log('✅ Token refreshed successfully')
				dispatch(setAccessToken(newToken))
				
				if (typeof localStorage !== 'undefined') {
					localStorage.setItem('access_token', newToken)
				}
				
				const newUrl = buildSocketUrl(newToken, station)
				latestSocketUrl.current = newUrl
				
				reconnectDelay.current = 1000
				shouldReconnect.current = true
				
				if (ws.current) {
					shouldReconnect.current = false
					ws.current.close()
					ws.current = null
				}

				setTimeout(() => {
					shouldReconnect.current = true
					connect(newUrl)
				}, 100)
				
			} else {
				console.error('❌ Token refresh returned no token')
				shouldReconnect.current = false
			}
		} catch (err) {
			console.error('❌ Token refresh failed:', err)
			shouldReconnect.current = false
			
			if (err?.status === 401 || err?.status === 403) {
				console.warn('🚫 Refresh token is invalid - user needs to re-authenticate')
			} else {
				console.warn('🔄 Network error during token refresh - will retry later')
				shouldReconnect.current = true
			}
		} finally {
			isRefreshingToken.current = false
		}
	}, [station, dispatch, buildSocketUrl])
	
	const forceReconnect = useCallback(() => {
		console.warn('🔄 Force reconnect triggered...')
		
		if (ws.current) {
			shouldReconnect.current = false
			ws.current.close()
			ws.current = null
		}
		
		clearInterval(pingIntervalId.current)
		clearTimeout(reconnectTimeoutId.current)
		
		reconnectDelay.current = 1000
		shouldReconnect.current = true
		
		reconnectWithNewToken()
	}, [reconnectWithNewToken])
	
	const connect = useCallback((url) => {
		if (ws.current) {
			console.warn('⚠️ WebSocket already exists. Skipping new connection.')
			return
		}
		
		if (!url) {
			console.error('❌ Cannot connect - no URL provided')
			return
		}
		
		console.log('🔌 Connecting to WebSocket:', url)
		const socketInstance = new WebSocket(url)
		ws.current = socketInstance
		setSocketInstance(socketInstance)
		
		socketInstance.addEventListener('open', () => {
			console.log('✅ WebSocket connected:', url)
			
			clearInterval(pingIntervalId.current)
			
			pingIntervalId.current = setInterval(() => {
				if (ws.current?.readyState === WebSocket.OPEN) {
					try {
						ws.current.send(JSON.stringify({type: 'ping'}))
					} catch (e) {
						console.warn('⚠️ Ping failed:', e)
					}
				}
			}, 55 * 1000)
			
			reconnectDelay.current = 1000
		})
		
		socketInstance.addEventListener('message', (event) => {
			try {
				const data = JSON.parse(event.data)
				
				if (data?.code === 401 || data?.type === 'auth_error') {
					console.warn('🚫 Received auth error. Attempting token refresh...')
					if (!isRefreshingToken.current) {
						forceReconnect()
					}
				}
			} catch (e) {
				console.error('⚠️ Message parse error', e)
			}
		})
		
		socketInstance.addEventListener('close', (event) => {
			console.warn('🔌 WebSocket closed', {
				code: event.code,
				reason: event.reason
			})
			setSocketInstance(null)
			clearInterval(pingIntervalId.current)
			ws.current = null
			
			if (shouldReconnect.current) {
				reconnectTimeoutId.current = setTimeout(() => {
					console.log('🔁 Reconnecting WebSocket...')
					const urlToUse = latestSocketUrl.current || buildSocketUrl(access_token, station)
					connect(urlToUse)
				}, reconnectDelay.current)
				
				reconnectDelay.current = Math.min(reconnectDelay.current * 2, 30000)
			}
		})
		
		socketInstance.addEventListener('error', (e) => {
			console.error('❗ WebSocket error:', e)
			
			if (ws.current?.readyState !== WebSocket.CLOSED) {
				ws.current?.close()
			}
		})
	}, [forceReconnect, access_token, station, buildSocketUrl])
	
	useEffect(() => {
		if (skipAuth || access_token) {
			const url = buildSocketUrl(access_token, station)
			latestSocketUrl.current = url
			
			if (!ws.current) {
				connect(url)
			} else if (ws.current && latestSocketUrl.current !== url) {
				console.log('🔄 URL changed, reconnecting...')
				shouldReconnect.current = false
				ws.current.close()
			}
		}
	}, [access_token, station, skipAuth, connect, buildSocketUrl])
	
	useEffect(() => {
		return () => {
			console.log('🧹 Cleaning up WebSocket hook')
			shouldReconnect.current = false
			clearInterval(pingIntervalId.current)
			clearTimeout(reconnectTimeoutId.current)
			
			if (ws.current) {
				ws.current.close()
				ws.current = null
			}
			
			setSocketInstance(null)
		}
	}, [])
	
	return {
		ws: socketInstance,
		setStation,
		forceReconnect,
		isConnected: socketInstance?.readyState === WebSocket.OPEN,
		isConnecting: socketInstance?.readyState === WebSocket.CONNECTING
	}
}